/**
 * Semantic mutations.
 *
 * A mutation describes *intent* ("tick the task that says X"), never a text
 * diff. That distinction is what makes conflict resolution possible: when a
 * write loses a race, we re-read the file and re-apply the intent against fresh
 * content. A text diff would have to be rebased and could silently corrupt;
 * an intent just runs again.
 *
 * `applyMutation` is pure — content in, content out, no I/O. So the same
 * function serves the optimistic UI update, the eventual write, and the retry
 * after a conflict, and it can be tested without a vault at all.
 */

import {
  appendTask,
  parseTasks,
  patchFrontmatter,
  removeTask,
  setTaskChecked,
  setTaskText,
} from './markdown.js';

export type Mutation =
  | { kind: 'toggleTask'; path: string; line: number; text: string; checked: boolean }
  | { kind: 'setTaskText'; path: string; line: number; text: string; to: string }
  | { kind: 'addTask'; path: string; text: string }
  | { kind: 'removeTask'; path: string; line: number; text: string }
  | { kind: 'patchFrontmatter'; path: string; patch: Record<string, unknown> }
  | { kind: 'writeFile'; path: string; content: string };

export class MutationConflictError extends Error {
  override readonly name = 'MutationConflictError';
}

/**
 * Locate the line a task mutation refers to.
 *
 * Tries the recorded line first, then falls back to finding the task by its
 * text. The fallback is the whole point: after a conflict the file may have
 * gained or lost lines above ours, so the index is stale but the text still
 * identifies the task. An ambiguous match (the same text twice) refuses rather
 * than guessing — ticking the wrong item is worse than not ticking.
 */
export function resolveTaskLine(content: string, line: number, text: string): number {
  const tasks = parseTasks(content);

  const atLine = tasks.find((task) => task.line === line);
  if (atLine && atLine.text === text) return line;

  const byText = tasks.filter((task) => task.text === text);
  if (byText.length === 1) return byText[0]!.line;
  if (byText.length > 1) {
    throw new MutationConflictError(
      `"${text}" appears ${byText.length} times — cannot tell which one was meant`,
    );
  }

  throw new MutationConflictError(`Task "${text}" is no longer in the file`);
}

/** Apply a mutation to file content. Pure. Throws on conflict. */
export function applyMutation(content: string, mutation: Mutation): string {
  switch (mutation.kind) {
    case 'toggleTask': {
      const line = resolveTaskLine(content, mutation.line, mutation.text);
      return setTaskChecked(content, line, mutation.checked, mutation.text);
    }
    case 'setTaskText': {
      const line = resolveTaskLine(content, mutation.line, mutation.text);
      return setTaskText(content, line, mutation.to, mutation.text);
    }
    case 'addTask':
      return appendTask(content, mutation.text);
    case 'removeTask': {
      const line = resolveTaskLine(content, mutation.line, mutation.text);
      return removeTask(content, line, mutation.text);
    }
    case 'patchFrontmatter':
      return patchFrontmatter(content, mutation.patch);
    case 'writeFile':
      return mutation.content;
  }
}

/**
 * Collapse a queue of mutations into the fewest that produce the same result.
 *
 * Dragging a goal slider or double-tapping a checkbox can enqueue dozens of
 * mutations in a second; without this, each becomes its own commit. Only
 * adjacent same-target mutations collapse, so ordering semantics are preserved.
 */
export function coalesce(mutations: Mutation[]): Mutation[] {
  const out: Mutation[] = [];

  for (const mutation of mutations) {
    const previous = out[out.length - 1];
    if (!previous || previous.path !== mutation.path) {
      out.push(mutation);
      continue;
    }

    // Repeated toggles of one task: only the final state matters.
    if (
      previous.kind === 'toggleTask' &&
      mutation.kind === 'toggleTask' &&
      previous.text === mutation.text
    ) {
      out[out.length - 1] = mutation;
      continue;
    }

    // Successive frontmatter patches merge into one.
    if (previous.kind === 'patchFrontmatter' && mutation.kind === 'patchFrontmatter') {
      out[out.length - 1] = {
        kind: 'patchFrontmatter',
        path: previous.path,
        patch: { ...previous.patch, ...mutation.patch },
      };
      continue;
    }

    // A whole-file write supersedes anything queued before it for that file.
    if (mutation.kind === 'writeFile') {
      out[out.length - 1] = mutation;
      continue;
    }

    out.push(mutation);
  }

  return out;
}

/** One-line summary used as the git commit message. */
export function describeMutation(mutation: Mutation): string {
  switch (mutation.kind) {
    case 'toggleTask':
      return `${mutation.checked ? 'Complete' : 'Reopen'} "${truncate(mutation.text)}"`;
    case 'setTaskText':
      return `Edit task in ${mutation.path}`;
    case 'addTask':
      return `Add "${truncate(mutation.text)}"`;
    case 'removeTask':
      return `Remove "${truncate(mutation.text)}"`;
    case 'patchFrontmatter':
      return `Update ${Object.keys(mutation.patch).join(', ')} in ${mutation.path}`;
    case 'writeFile':
      return `Edit ${mutation.path}`;
  }
}

function truncate(text: string, max = 50): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}
